import {
  PermissionValues,
  useCurrentAccount,
  useCurrentWorkspace,
  useWorkspaceRoutine,
} from '@gripp/shared-logic';
import {
  Colors,
  LoadingIndicator,
  RoutineForm,
  RoutineModules,
  Spacing,
} from '@gripp/shared-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { Button } from 'react-native-paper';
import { useNavigate, useParams } from 'react-router-dom';
import { CopyToWorkspaceButton } from './copyToWorkspaceButton';
import { DeleteRoutineButton } from './deleteRoutineButton';

type RoutinePreviewColumnProps = {
  basePath?: string;
};

export const RoutinePreviewColumn = ({
  basePath = '/new-routines',
}: RoutinePreviewColumnProps) => {
  const { routineId } = useParams<{ routineId: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  const currentWorkspace = useCurrentWorkspace();
  const routine = useWorkspaceRoutine(routineId ?? '');

  const isGlobalAdmin = useMemo(
    () => !!(account && account.hasPermission(PermissionValues.globalAdmin)),
    [account]
  );

  if (!routineId || !routine) {
    return <LoadingIndicator />;
  }

  const module = routine.type ? RoutineModules[routine.type] : undefined;

  if (!module) {
    return null;
  }

  const rightFormButton = isGlobalAdmin ? (
    <Button
      style={styles.button}
      labelStyle={styles.buttonLabel}
      onPress={() => navigate(`${basePath}/${routineId}/edit`)}
      buttonColor={Colors.primary}
    >
      {t('topNav.modify')}
    </Button>
  ) : undefined;

  const footer = isGlobalAdmin ? (
    <View style={styles.footer}>
      <DeleteRoutineButton
        onSelected={() => navigate(basePath)}
        workspaceRoutine={routine}
      />
      <CopyToWorkspaceButton
        onSelected={() => navigate(basePath)}
        workspaceRoutine={routine}
      />
    </View>
  ) : undefined;

  return (
    <View style={styles.container}>
      <RoutineForm
        workspaceAsset={{ workspace: currentWorkspace } as any}
        module={module}
        routine={routine}
        onCompleted={async () => {
          navigate(basePath);
        }}
        onCancel={() => navigate(basePath)}
        topBar={() => <></>}
        preview={true}
        rightButton={rightFormButton}
        footer={footer}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  button: {
    borderRadius: 8,
    width: '100%',
    maxWidth: 100,
    height: 44,
    justifyContent: 'center',
    marginRight: Spacing.baseMargin.base,
  },
  buttonLabel: {
    fontSize: 16,
    fontWeight: '500',
    color: Colors.white,
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
